import { useMemo, useState } from "react";
import { PermissionGate } from "../../components/PermissionGate";
import {
  EMPTY_ENGAGEMENT_FILTERS,
  MIN_AGGREGATE_SIZE,
  STATUS_COLORS,
  buildAcademicUnitSummary,
  buildEngagementOptions,
  buildEngagementSummary,
  filterEngagementRows,
  formatNumber,
  formatPercent,
  ratio,
  snapshotRows
} from "./engajamentoModel";
import {
  EngagementEmpty,
  EngagementError,
  EngagementFilterBar,
  EngagementHeader,
  EngagementLoading,
  EngagementMetricCard,
  EngagementPanel,
  EngagementPrivacyEmpty
} from "./EngagementUi";
import { useEngagementSnapshot } from "./useEngagementSnapshot";
import type { AcademicUnitSummary, EngagementFilters } from "./types";
import "./engajamento.css";

export function IndicadoresPage() {
  const { snapshot, loading, error, retry } = useEngagementSnapshot();
  const [filters, setFilters] = useState<EngagementFilters>({ ...EMPTY_ENGAGEMENT_FILTERS });
  const options = useMemo(() => snapshot ? buildEngagementOptions(snapshot) : emptyOptions(), [snapshot]);
  const rows = useMemo(() => snapshot ? filterEngagementRows(snapshotRows(snapshot), filters) : [], [filters, snapshot]);
  const summary = useMemo(() => buildEngagementSummary(rows, snapshot?.data_geracao), [rows, snapshot?.data_geracao]);
  const units = useMemo(() => buildAcademicUnitSummary(rows), [rows]);

  function updateFilter(key: keyof EngagementFilters, value: string) {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  return (
    <PermissionGate permission="dashboard">
      <div className="engagement-page">
        {loading ? <EngagementLoading /> : null}
        {error ? <EngagementError message={error} onRetry={retry} /> : null}
        {snapshot && !loading ? (
          <>
            <EngagementHeader
              eyebrow="Visão operacional"
              title="Indicadores de engajamento"
              description="Panorama agregado para priorização acadêmica, acompanhamento e acesso ao ambiente virtual."
              generatedAt={snapshot.data_geracao}
            />
            <EngagementFilterBar
              filters={filters}
              options={options}
              total={summary.total}
              onChange={updateFilter}
              onReset={() => setFilters({ ...EMPTY_ENGAGEMENT_FILTERS })}
            />

            {!summary.total ? <EngagementEmpty /> : summary.total < MIN_AGGREGATE_SIZE ? <EngagementPrivacyEmpty /> : (
              <>
                <section className="engagement-metrics" aria-label="Indicadores principais">
                  <EngagementMetricCard
                    label="Estudantes no recorte"
                    value={formatNumber(summary.total)}
                    detail="Registros agregados e únicos na base"
                    calculation="Quantidade de estudantes distintos presentes no snapshot após a aplicação dos filtros selecionados."
                    tone="blue"
                    icon="students"
                  />
                  <EngagementMetricCard
                    label="Ativos"
                    value={formatPercent(ratio(summary.active, summary.total))}
                    detail={`${formatNumber(summary.active)} estudantes`}
                    calculation="Estudantes classificados como Ativo divididos pelo total de estudantes do recorte, multiplicado por 100."
                    tone="green"
                    icon="check"
                  />
                  <EngagementMetricCard
                    label="Em suporte"
                    value={formatPercent(ratio(summary.support, summary.total))}
                    detail={`${formatNumber(summary.support)} estudantes · ${formatNumber(summary.criticalSupport)} críticos`}
                    calculation="Soma dos estudantes classificados como Suporte e Suporte Crítico, dividida pelo total do recorte e multiplicada por 100."
                    tone="yellow"
                    icon="support"
                  />
                  <EngagementMetricCard
                    label="Nunca acessaram"
                    value={formatPercent(ratio(summary.neverAccessed, summary.total))}
                    detail={`${formatNumber(summary.neverAccessed)} estudantes`}
                    calculation="Estudantes classificados como Nunca Acessou divididos pelo total de estudantes do recorte, multiplicado por 100."
                    tone="red"
                    icon="noAccess"
                  />
                  <EngagementMetricCard
                    label="Cancelados"
                    value={formatPercent(ratio(summary.cancelled, summary.total))}
                    detail={`${formatNumber(summary.cancelled)} estudantes`}
                    calculation="Estudantes com status ou situação acadêmica de cancelamento divididos pelo total do recorte, multiplicado por 100."
                    tone="slate"
                    icon="cancel"
                  />
                  <EngagementMetricCard
                    label="Não sincronizados"
                    value={formatPercent(ratio(summary.notSynchronized, summary.total))}
                    detail={`${formatNumber(summary.notSynchronized)} estudantes`}
                    calculation="Estudantes classificados como Não sincronizado divididos pelo total do recorte, multiplicado por 100."
                    tone="purple"
                    icon="sync"
                  />
                </section>

                <section className="engagement-indicator-grid">
                  <EngagementPanel title="Composição do recorte" subtitle="Cada faixa exibe quantidade e participação percentual" help="Cada faixa conta os estudantes da situação indicada. O percentual divide essa quantidade pelo total de estudantes após os filtros e multiplica por 100.">
                    <div className="engagement-distribution" role="img" aria-label="Distribuição percentual dos estudantes por status">
                      {summary.status.map((item) => (
                        <div className="engagement-distribution__row" key={item.label}>
                          <div className="engagement-distribution__label">
                            <span style={{ background: STATUS_COLORS[item.label as keyof typeof STATUS_COLORS] || STATUS_COLORS.Outros }} />
                            <strong>{item.label}</strong>
                          </div>
                          <div className="engagement-distribution__value">
                            <strong>{formatNumber(item.value)}</strong>
                            <span>{formatPercent(item.share)}</span>
                          </div>
                          <div className="engagement-distribution__track" aria-hidden="true">
                            <span style={{ width: `${Math.max(item.share, 1)}%`, background: STATUS_COLORS[item.label as keyof typeof STATUS_COLORS] || STATUS_COLORS.Outros }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </EngagementPanel>

                  <EngagementPanel title="Pontos de atenção" subtitle="Sinais descritivos para orientar o acompanhamento">
                    <div className="engagement-insights">
                      <Insight
                        tone="red"
                        title={`${formatNumber(summary.criticalSupport)} em suporte crítico`}
                        text={`${formatPercent(ratio(summary.criticalSupport, summary.total))} do recorte requer atenção prioritária segundo a classificação vigente.`}
                      />
                      <Insight
                        tone="orange"
                        title={`${formatNumber(summary.neverAccessed)} sem primeiro acesso`}
                        text={`${formatPercent(ratio(summary.neverAccessed, summary.total))} ainda não registrou acesso ao ambiente virtual.`}
                      />
                      <Insight
                        tone="blue"
                        title={units[0] ? `${units[0].label}: maior base` : "Unidade sem destaque"}
                        text={units[0] ? `${formatNumber(units[0].total)} estudantes, equivalentes a ${formatPercent(ratio(units[0].total, summary.total))} do recorte.` : "Não há unidade acadêmica suficiente para análise agregada."}
                      />
                    </div>
                    <p className="engagement-privacy-note">Resultados são agregados. Grupos com menos de cinco estudantes não são exibidos isoladamente nos rankings.</p>
                  </EngagementPanel>

                  <EngagementPanel
                    className="engagement-panel--wide"
                    title="Leitura por unidade acadêmica"
                    subtitle="Volume e percentuais calculados dentro de cada unidade"
                  >
                    <AcademicUnitTable rows={units} />
                  </EngagementPanel>
                </section>
              </>
            )}
          </>
        ) : null}
      </div>
    </PermissionGate>
  );
}

function AcademicUnitTable({ rows }: { rows: AcademicUnitSummary[] }) {
  if (!rows.length) return <EngagementEmpty>Não há unidades com volume suficiente para exibição agregada.</EngagementEmpty>;
  return (
    <div className="engagement-table-wrap">
      <table className="engagement-summary-table">
        <thead>
          <tr>
            <th>Unidade acadêmica</th>
            <th>Estudantes</th>
            <th>Ativos</th>
            <th>Em suporte</th>
            <th>Nunca acessaram</th>
            <th>Cancelados</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label}>
              <td><strong>{row.label}</strong></td>
              <td>{formatNumber(row.total)}</td>
              <TableMetric value={row.active} share={row.activeRate} tone="green" />
              <TableMetric value={row.support} share={row.supportRate} tone="yellow" />
              <TableMetric value={row.neverAccessed} share={row.neverAccessedRate} tone="red" />
              <TableMetric value={row.cancelled} share={row.cancelledRate} tone="slate" />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableMetric({ value, share, tone }: { value: number; share: number; tone: string }) {
  return <td><span className={`engagement-table-metric engagement-table-metric--${tone}`}>{formatNumber(value)} <small>{formatPercent(share)}</small></span></td>;
}

function Insight({ tone, title, text }: { tone: "red" | "orange" | "blue"; title: string; text: string }) {
  return (
    <article className={`engagement-insight engagement-insight--${tone}`}>
      <span />
      <div><strong>{title}</strong><p>{text}</p></div>
    </article>
  );
}

function emptyOptions() {
  return { periodo: [], polo: [], curso: [], ua: [] };
}
